import { versionSortKey, versionTier } from "./format.js";

/*
 * Filtering and derivation for the Users page.
 *
 * Out of the component for the same reason as lib/dashboard.js: these decide
 * which rows a person sees, and a filter that silently matches nothing is the
 * easiest bug in the app to ship. The current page has exactly that — it
 * compares a POPULATED team object against a team id string, so selecting a
 * team always empties the table. See `teamIdOf`.
 */

/**
 * The list endpoint populates `team` as { _id, name }, but the id is what the
 * filter and the Teams page deal in. Everything that touches a user's team goes
 * through these two so the object/string confusion cannot come back.
 */
export const teamIdOf = (user) => user?.team?._id ?? user?.team ?? null;
export const teamNameOf = (user) => user?.team?.name ?? null;

/** OS reported by the desktop app, e.g. "Mac OS" / "Windows" / "Linux". */
export const osOf = (user) => user?.systemInfo?.os ?? null;

export const appVersionOf = (user) => user?.systemInfo?.appVersion ?? null;

/** Which of the four version buckets this user's desktop build falls into. */
export const tierOf = (user, latest) => versionTier(appVersionOf(user), latest);

export const USER_SORTS = {
    name: (u) => (u.name || "").toLowerCase(),
    email: (u) => (u.email || "").toLowerCase(),
    role: (u) => (u.role || "").toLowerCase(),
    team: (u) => (teamNameOf(u) || "￿").toLowerCase(), // unassigned sorts last
    os: (u) => (osOf(u) || "￿").toLowerCase(),
    version: (u) => versionSortKey(appVersionOf(u)),
    // Never-active sorts last on descending, which is where it belongs: the
    // question is always "who was here recently".
    lastActive: (u) => (u.lastActive ? new Date(u.lastActive).getTime() : 0),
};

/** Every filter the page offers. An empty string means "not filtering on this". */
/*
 * WHO MAY STILL BE CHOSEN.
 *
 * A deactivated account cannot be tracked, cannot start work and cannot be
 * logged into, so offering one in a picker creates a team member, a project
 * assignee or a schedule subject that will never do anything. The page this
 * replaces filtered for exactly this at every such list —
 *
 *     teams      users.filter(u => u.isActive && u.team == null)
 *     projects   users.filter(u => u.isActive)
 *     schedules  users.filter(u => u.role == "employee" && u.isActive)
 *
 * — and the port dropped it, which is how deactivated accounts came back.
 *
 * One helper, because the rule went missing five separate times when it was
 * five separate filters. It is NOT for lists that look at the past: a departed
 * employee's screenshots and reports still exist and must stay reachable.
 */
export const activeOnly = (users = []) => users.filter((u) => u.isActive !== false);

export const EMPTY_FILTERS = {
    search: "",
    role: "",
    status: "active", // the current page defaults to active-only too
    team: "",
    os: "",
    version: "",
};

export function filterUsers(users, filters, latest) {
    const q = filters.search.trim().toLowerCase();

    return users.filter((user) => {
        if (filters.role && user.role !== filters.role) return false;
        if (filters.status && user.isActive !== (filters.status === "active")) return false;
        if (filters.team && teamIdOf(user) !== filters.team) return false;
        if (filters.os && osOf(user) !== filters.os) return false;
        if (filters.version && tierOf(user, latest) !== filters.version) return false;

        if (q) {
            const haystack = `${user.name} ${user.email} ${user.role} ${teamNameOf(user) || ""}`;
            if (!haystack.toLowerCase().includes(q)) return false;
        }
        return true;
    });
}

/** The four stat cards. Counted over ALL users, not the filtered view. */
export function userStats(users, latest) {
    return {
        total: users.length,
        active: users.filter((u) => u.isActive).length,
        neverInstalled: users.filter((u) => !u.systemInfo).length,
        outdated: users.filter((u) => tierOf(u, latest) === "stale").length,
    };
}

/** The distinct operating systems present, for the OS filter's options. */
export function osOptions(users) {
    return [...new Set(users.map(osOf).filter(Boolean))].sort();
}
